import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../../utils/AxiosHelper';
import { useAppSelector } from '../hooks';
import API from '../../modules/settings/APIs';
import { API_PARAMS } from "../../constants/constants";

export const impactAssessmentKeys = {
  groups: (params?: Record<string, any>) => ['impact-assessment', 'groups', params] as const,
  variablesGroup: (groupId?: string | number, params?: Record<string, any>) => ['impact-assessment', 'variables-group', groupId, params] as const,
  variables: (variableGroupId?: string | number, params?: Record<string, any>) => ['impact-assessment', 'variables', variableGroupId, params] as const,
  values: (params?: Record<string, any>) => ['impact-assessment', 'values', params] as const,
} as const;

// Custom hook for fetching groups
export const useGroups = (params?: Record<string, any>) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: impactAssessmentKeys.groups(params),
    queryFn: async () => {
      const defaultParams = {
        state: API_PARAMS.IS_ACTIVE,
        domainId: '1',
        type: API_PARAMS.IMPACT.ISSUES_ENTITY,
      };

      const response = await apiRequest.get(API.parameters.config.assessment.groups, {
        params: { ...defaultParams, ...params }
      });
      return response.data.data || response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });
};

// Custom hook for fetching variables by group
export const useVariablesGroup = (groupId?: string | number, params?: Record<string, any>) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: impactAssessmentKeys.variablesGroup(groupId, params),
    queryFn: async () => {
      const defaultParams = {
        type: API_PARAMS.IMPACT.ISSUES_ENTITY,
        state: API_PARAMS.IS_ACTIVE,
        node: API_PARAMS.ROOT
      };

      const response = await apiRequest.get(API.parameters.config.assessment.variables_group, {
        params: { ...defaultParams, ...params }
      });
      return response.data.data || response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: isAuthenticated && !!token,
  });
};

// Custom hook for fetching variables
export const useVariables = (variableGroupId?: string | number, params?: Record<string, any>) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  return useQuery({
    queryKey: impactAssessmentKeys.variables(variableGroupId, params),
    queryFn: async () => {
      const defaultParams = { variableGroupId };

      const response = await apiRequest.get(API.parameters.config.assessment.variables, {
        params: { ...defaultParams, ...params }
      });
      return response.data.data || response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!variableGroupId && isAuthenticated && !!token,
  });
};

// Custom hook for fetching values
export const useValues = (params?: Record<string, any>) => {
  const { isAuthenticated, token } = useAppSelector((state) => state.session);

  // Only enable the query if the params is defined AND has either idVariable or idVariableGroup
  const hasValidParams = params !== undefined &&
                         params !== null &&
                         (params.idVariable !== undefined || params.idVariableGroup !== undefined);

  return useQuery({
    queryKey: impactAssessmentKeys.values(params),
    queryFn: async () => {
      const defaultParams = {
        idVariable: params?.idVariable,
        idVariableGroup: params?.idVariableGroup,
        start: 0,
        limit: 25
      };

      const response = await apiRequest.get(API.parameters.config.assessment.values, {
        params: { ...defaultParams, ...params }
      });
      return response.data.data || response.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: hasValidParams && isAuthenticated && !!token,
  });
};

const invalidateAssessmentTree = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'groups'] });
  queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables-group'] });
  queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables'] });
};

// Custom hook for creating a group
export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(
        API.parameters.config.assessment.groups,
        data,
        {
          params: {
            type: API_PARAMS.IMPACT.ISSUES_ENTITY,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => invalidateAssessmentTree(queryClient),
  });
};

// Custom hook for updating a group
export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: any }) => {
      const response = await apiRequest.put(`${API.parameters.config.assessment.groups}/${id}`, data);
      return response.data;
    },
    onSuccess: () => invalidateAssessmentTree(queryClient),
  });
};

// Custom hook for deleting a group
export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await apiRequest.delete(`${API.parameters.config.assessment.groups}/${id}`);
      return response.data;
    },
    onSuccess: () => invalidateAssessmentTree(queryClient),
  });
};

// Custom hook for creating a variable
export const useCreateVariable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(API.parameters.config.assessment.variables, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables-group'] });
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables'] });
    },
  });
};

// Custom hook for updating a variable
export const useUpdateVariable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: any }) => {
      const response = await apiRequest.put(`${API.parameters.config.assessment.variables}/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables-group'] });
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables'] });
    },
  });
};

// Custom hook for deleting a variable
export const useDeleteVariable = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await apiRequest.delete(`${API.parameters.config.assessment.variables}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables-group'] });
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'variables'] });
    },
  });
};

// Custom hook for creating a value
export const useCreateValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest.post(API.parameters.config.assessment.values, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'values'] });
    },
  });
};

// Custom hook for updating a value
export const useUpdateValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string | number; data: any }) => {
      const response = await apiRequest.put(`${API.parameters.config.assessment.values}/${id}`, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'values'] });
    },
  });
};

// Custom hook for deleting a value
export const useDeleteValue = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string | number) => {
      const response = await apiRequest.delete(`${API.parameters.config.assessment.values}/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['impact-assessment', 'values'] });
    },
  });
};

